import { chain, find, forEach, map, merge } from 'lodash';
import * as BaseFilter from './Filters/BaseFilter';
import { getRelevantColumnMetadata } from './Filters/BaseFilter';
import { SoqlFilter } from 'common/components/FilterBar/SoqlFilter';
import { FilterBarColumns } from 'common/components/FilterBar/index';
import { DataSourceCCVs, FilterItemType, FilterParameterConfiguration } from 'common/types/reportFilters';
import { FilterBarColumn } from '../types';
import isEmpty from 'lodash/isEmpty';
import some from 'lodash/some';
import { findClientContextVariableDefault } from './Filters/parameterUtils';

export function getNonHiddenFilters(filters: SoqlFilter[], isReadOnly = true) {
  return chain(filters)
    .map((filterItem) => {
      if (isReadOnly && filterItem.isHidden) {
        return null;
      }
      return filterItem;
    })
    .compact()
    .value();
}

export function resetFiltersToDefaults(
  filters: SoqlFilter[],
  columns: FilterBarColumns,
  resetOnlyVisibleFilters = false,
) {
  return map(filters, (filter) => {
    // We only want to pass in the columns associated to the filter so we manually map the columns for each filter
    const filterColumns: Record<string, FilterBarColumn> = {};
    forEach(filter.columns, (column) => {
      filterColumns[column.datasetUid] = find(columns[column.datasetUid], ['fieldName', column.fieldName])!;
    });
    return BaseFilter.reset(filter, filterColumns, resetOnlyVisibleFilters);
  });
}

export function resetAllFiltersToDefaults(
  filterParameterConfigurations: FilterParameterConfiguration[],
  columns: FilterBarColumns,
  clientContextVariables: DataSourceCCVs | undefined,
  editMode: boolean,
  resetOnlyVisibleFilters = false,
  computedColumns: FilterBarColumn[] = [],
): FilterParameterConfiguration[] {
  return map(filterParameterConfigurations, (filterParameterConfiguration) => {
    if (filterParameterConfiguration.type === FilterItemType.FILTER) {
      const filterColumns = getRelevantColumnMetadata(
        filterParameterConfiguration.config,
        columns,
        computedColumns,
      );
      const newFilter = BaseFilter.reset(
        filterParameterConfiguration.config,
        filterColumns,
        resetOnlyVisibleFilters,
      );
      return {
        type: FilterItemType.FILTER,
        config: newFilter,
      };
    } else if (!filterParameterConfiguration.config.isHidden) {
      let newParameter = { ...filterParameterConfiguration.config };
      const resetValue = findClientContextVariableDefault(
        filterParameterConfiguration.config.paramIds,
        clientContextVariables,
        editMode,
      );
      newParameter = merge(newParameter, { overrideValue: resetValue });
      return {
        type: FilterItemType.PARAMETER,
        config: newParameter,
      };
    } else {
      return filterParameterConfiguration;
    }
  });
}

// Helper function to check if a filter item has changes
export const doesFilterHaveResettableChanges = (
  item: FilterParameterConfiguration,
  editMode: boolean,
  isReadOnly?: boolean,
  clientContextVariables?: DataSourceCCVs,
) => {
  if (item.type === FilterItemType.FILTER) {
    // For filter items, check if they are visible, have values, and can be changed
    const isVisible = !isReadOnly || !item.config.isHidden;
    const hasActiveValue = !isEmpty(item.config.arguments);
    const isModifiable = !item.config.isOverridden;

    return isVisible && hasActiveValue && isModifiable;
  }

  // For non-filter items (like context variables)
  if (item.config.isHidden) {
    return false; // Hidden items can't be changed
  }

  const defaultValue = findClientContextVariableDefault(
    item.config.paramIds,
    clientContextVariables,
    editMode,
  );
  const currentValue = item.config.overrideValue;
  return defaultValue !== currentValue;
};

// iterates over a collection of filters and if any have changes it returns true
export const doAnyFiltersHaveResettableChanges = (
  filters: FilterParameterConfiguration[],
  editMode: boolean,
  clientContextVariables?: DataSourceCCVs,
  isReadOnly?: boolean,
) => {
  return some(filters, (filter) =>
    doesFilterHaveResettableChanges(filter, editMode, isReadOnly, clientContextVariables),
  );
};
